import React, { useState, useEffect } from "react";
import { UserAuth } from "../context/AuthContext";
import { useNavigate, Link } from "react-router-dom";
import { ref, uploadBytes, getDownloadURL } from "firebase/storage";
import { storage } from "../firebase";
import { v4 } from "uuid";
import HeaderComp from "./Components/HeaderComp";
import Footer from "./Components/Footer";
import ModeSwitch from "./Components/ModeSwitch";
import { useLang } from "../i18n/LanguageContext";
import { FaBook, FaWallet, FaChalkboardTeacher, FaUserEdit, FaSignOutAlt, FaCamera, FaDownload, FaUserSlash } from "react-icons/fa";
import { collection, getDocs, query, where } from "firebase/firestore";
import { db } from "../firebase";
import { LEGAL } from "../legal/config";

const Account = () => {
  const navigate = useNavigate();
  const { t } = useLang();
  const { logOut, user, currentMode, updateUserProfile, fetchUserProfile, updateUserProfilePicture, getAllCoursStudentByStudentMail } = UserAuth();
  const [userProfile, setUserProfile] = useState(null);
  const [showEditModal, setShowEditModal] = useState(false);
  const [editData, setEditData] = useState({ firstName: "", lastName: "", dateOfBirth: "", description: "" });
  const [saving, setSaving] = useState(false);
  const [successMsg, setSuccessMsg] = useState("");

  const [uploadError, setUploadError] = useState("");

  useEffect(() => {
    if (!user) return; // la route est protégée par ProtectedRoute
    fetchUserProfile().then((p) => {
      if (p) {
        setUserProfile(p);
        setEditData({
          firstName: p.firstName || "",
          lastName: p.lastName || "",
          dateOfBirth: p.dateOfBirth || "",
          description: p.description || "",
        });
      }
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.uid]);

  const handleSignOut = async () => {
    await logOut();
    navigate("/");
  };

  const handleImageChange = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setUploadError("");
    if (!file.type.startsWith("image/")) {
      setUploadError(t("Le fichier doit être une image."));
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      setUploadError(t("L'image ne doit pas dépasser 5 Mo."));
      return;
    }
    try {
      const imageRef = ref(storage, `profilePictures/${user.uid}/${v4()}-${file.name}`);
      await uploadBytes(imageRef, file);
      const url = await getDownloadURL(imageRef);
      await updateUserProfilePicture(url);
      setUserProfile((prev) => ({ ...prev, profilePicture: url }));
    } catch (err) {
      console.error(err);
      setUploadError(t("Impossible d'envoyer la photo. Réessayez plus tard."));
    }
  };

  const handleSave = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await updateUserProfile(editData.dateOfBirth, editData.description, editData.firstName, editData.lastName);
      setUserProfile((prev) => ({ ...prev, ...editData }));
      setSuccessMsg(t("Profil mis à jour"));
      setTimeout(() => setSuccessMsg(""), 3000);
      setShowEditModal(false);
    } catch (err) {
      console.error(err);
    } finally {
      setSaving(false);
    }
  };

  // RGPD — droit d'accès / portabilité : copie de mes données au format JSON
  const handleExport = async () => {
    try {
      const [profile, enrollments, created] = await Promise.all([
        fetchUserProfile(),
        getAllCoursStudentByStudentMail(user.email),
        getDocs(query(collection(db, "courses"), where("ownerId", "==", user.uid))).then((s) =>
          s.docs.map((d) => ({ id: d.id, ...d.data() }))
        ),
      ]);
      const data = {
        exportedAt: new Date().toISOString(),
        account: { uid: user.uid, email: user.email, ...profile },
        enrollments,
        coursesCreated: created.map(({ jitsiRoom, meetLink, ...c }) => c),
      };
      const url = URL.createObjectURL(new Blob([JSON.stringify(data, null, 2)], { type: "application/json" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = `tatie-soso-mes-donnees-${new Date().toISOString().slice(0, 10)}.json`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (err) {
      console.error(err);
      setUploadError(t("Impossible de préparer tes données. Réessaie plus tard."));
    }
  };

  // RGPD — droit à l'effacement : demande par e-mail (solde, cours et paiements
  // doivent être soldés à la main avant la suppression)
  const deletionMailto = LEGAL.contactEmail
    ? `mailto:${LEGAL.contactEmail}?subject=${encodeURIComponent("Suppression de mon compte Tatie Soso")}&body=${encodeURIComponent(
        `Bonjour,\n\nJe demande la fermeture de mon compte Tatie Soso et la suppression de mes données.\nE-mail du compte : ${user?.email || ""}\n\nMerci.`
      )}`
    : null;

  const isTeacher = currentMode === "prof";

  const MENU_ITEMS = [
    { icon: <FaBook />, label: t("Mes cours"), href: "/mes_cours" },
    { icon: <FaWallet />, label: t("Mon portefeuille"), href: "/portefeuille" },
    { icon: <FaChalkboardTeacher />, label: t("Cours à venir"), href: "/cours_a_venir" },
    ...(userProfile?.role === 0 ? [{ icon: <FaUserEdit />, label: t("Administration"), href: "/adminpage" }] : []),
  ];

  return (
    <div className="min-h-screen bg-creme flex flex-col">
      <HeaderComp />

      <div className="flex-1 max-w-4xl mx-auto w-full px-4 py-10">
        {/* Bandeau haut */}
        <div className="bg-prune rounded-2xl p-8 text-white flex flex-col md:flex-row items-center gap-6 mb-8">
          {/* Avatar */}
          <div className="relative shrink-0">
            <div className="w-28 h-28 rounded-full border-4 border-white overflow-hidden">
              {userProfile?.profilePicture ? (
                <img src={userProfile.profilePicture} alt={t("profil")} className="w-full h-full object-cover" />
              ) : (
                <div className="w-full h-full bg-prune-dark flex items-center justify-center text-4xl font-bold">
                  {userProfile?.firstName ? userProfile.firstName[0] : "?"}
                </div>
              )}
            </div>
            <label htmlFor="file-input" className="absolute bottom-0 right-0 w-8 h-8 bg-or rounded-full flex items-center justify-center cursor-pointer hover:bg-or-dark transition-colors">
              <FaCamera size={12} className="text-white" />
              <input id="file-input" type="file" accept="image/*" className="hidden" onChange={handleImageChange} />
            </label>
          </div>

          {/* Infos */}
          <div className="text-center md:text-left flex-1">
            <h1 className="text-2xl font-bold">
              {t("Bonjour {name} !", { name: [userProfile?.firstName, userProfile?.lastName].filter(Boolean).join(" ") })}
            </h1>
            <p className="text-white/80 text-sm mt-1">{user?.email}</p>
            <span className={`inline-block mt-2 text-xs font-semibold px-3 py-1 rounded-full ${isTeacher ? "bg-or text-prune-deep" : "bg-white text-prune"}`}>
              {currentMode === "admin" ? `🛡️ ${t("Administrateur")}` : isTeacher ? `🧑‍🏫 ${t("Mode prof")}` : `🎓 ${t("Mode élève")}`}
            </span>
            {userProfile?.description && (
              <p className="text-white/90 text-sm mt-3 italic">"{userProfile.description}"</p>
            )}
          </div>

          {/* Boutons */}
          <div className="flex flex-col gap-2 shrink-0">
            <button
              onClick={() => setShowEditModal(true)}
              className="flex items-center gap-2 px-4 py-2 bg-white text-prune rounded-full text-sm font-semibold hover:bg-stone-100 transition-colors"
            >
              <FaUserEdit /> {t("Modifier le profil")}
            </button>
            <button
              onClick={handleSignOut}
              className="flex items-center gap-2 px-4 py-2 border border-white/40 text-white rounded-full text-sm font-semibold hover:bg-white/10 transition-colors"
            >
              <FaSignOutAlt /> {t("Déconnexion")}
            </button>
          </div>
        </div>

        {/* Message succès */}
        {successMsg && (
          <div className="bg-green-50 border border-green-200 text-green-700 rounded-lg px-4 py-3 text-sm mb-4">
            ✓ {successMsg}
          </div>
        )}

        {/* Choix du mode */}
        {currentMode !== "admin" && (
          <div className="bg-white rounded-2xl shadow-sm p-6 mb-6">
            <h2 className="font-bold text-stone-800 mb-1">{t("Je veux…")}</h2>
            <p className="text-xs text-stone-500 mb-4">
              {user?.role === 1
                ? t("Vous pouvez suivre des cours et en donner. Changez de mode quand vous voulez.")
                : t("Vous êtes élève. Passez en mode prof pour proposer vos propres cours.")}
            </p>
            <ModeSwitch />
          </div>
        )}

        {uploadError && (
          <div className="bg-red-50 border border-red-200 text-red-700 rounded-lg px-4 py-3 text-sm mb-4">
            ⚠️ {uploadError}
          </div>
        )}

        {/* Grille de navigation */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          {MENU_ITEMS.map((item) => (
            <Link key={item.href} to={item.href}>
              <div className="bg-white rounded-2xl p-6 shadow-sm hover:shadow-carte-hover transition-shadow border-b-4 border-prune flex flex-col items-center gap-3 text-center cursor-pointer group">
                <div className="text-prune text-3xl group-hover:scale-110 transition-transform">{item.icon}</div>
                <span className="font-semibold text-stone-700">{item.label}</span>
              </div>
            </Link>
          ))}
        </div>

        {/* ─── Mes données (RGPD) ─── */}
        <section className="bg-white rounded-2xl shadow-sm p-6 mt-6">
          <h2 className="font-bold text-stone-800 mb-1">{t("Mes données personnelles")}</h2>
          <p className="text-sm text-stone-600 mb-4">
            {t("Tu peux récupérer une copie de tes données ou demander la fermeture de ton compte.")}{" "}
            <Link to="/confidentialite" className="text-prune underline">{t("Politique de confidentialité")}</Link>
          </p>
          <div className="flex flex-col sm:flex-row gap-3">
            <button
              onClick={handleExport}
              className="inline-flex items-center justify-center gap-2 px-5 py-2.5 rounded-full text-sm font-semibold text-prune border-2 border-prune hover:bg-prune hover:text-white transition-colors"
            >
              <FaDownload /> {t("Télécharger mes données")}
            </button>
            {deletionMailto ? (
              <a
                href={deletionMailto}
                className="inline-flex items-center justify-center gap-2 px-5 py-2.5 rounded-full text-sm font-semibold text-red-700 border-2 border-red-200 hover:bg-red-50 transition-colors"
              >
                <FaUserSlash /> {t("Demander la suppression de mon compte")}
              </a>
            ) : (
              <Link
                to="/confidentialite#droits"
                className="inline-flex items-center justify-center gap-2 px-5 py-2.5 rounded-full text-sm font-semibold text-red-700 border-2 border-red-200 hover:bg-red-50 transition-colors"
              >
                <FaUserSlash /> {t("Demander la suppression de mon compte")}
              </Link>
            )}
          </div>
        </section>
      </div>

      <Footer />

      {/* Modal d'édition */}
      {showEditModal && (
        <div className="fixed inset-0 bg-black bg-opacity-40 z-[60] flex items-start justify-center px-4 overflow-y-auto py-8">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md p-8 relative my-auto">
            <button onClick={() => setShowEditModal(false)} className="absolute top-4 right-4 text-stone-400 hover:text-stone-700 text-2xl">×</button>
            <h3 className="text-xl font-bold text-stone-800 mb-6">{t("Modifier mon profil")}</h3>
            <form onSubmit={handleSave} className="space-y-4">
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-semibold text-stone-600 mb-1">{t("Prénom")}</label>
                  <input name="firstName" type="text" value={editData.firstName} onChange={(e) => setEditData({ ...editData, firstName: e.target.value })}
                    className="w-full border border-stone-200 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-prune" />
                </div>
                <div>
                  <label className="block text-xs font-semibold text-stone-600 mb-1">{t("Nom")}</label>
                  <input name="lastName" type="text" value={editData.lastName} onChange={(e) => setEditData({ ...editData, lastName: e.target.value })}
                    className="w-full border border-stone-200 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-prune" />
                </div>
              </div>
              <div>
                <label className="block text-xs font-semibold text-stone-600 mb-1">{t("Date de naissance")}</label>
                <input name="dob" type="date" value={editData.dateOfBirth} onChange={(e) => setEditData({ ...editData, dateOfBirth: e.target.value })}
                  className="w-full border border-stone-200 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-prune" />
              </div>
              <div>
                <label className="block text-xs font-semibold text-stone-600 mb-1">{t("Description / Bio")}</label>
                <textarea name="description" rows={3} value={editData.description} onChange={(e) => setEditData({ ...editData, description: e.target.value })}
                  placeholder={t("Quelques mots sur toi...")}
                  className="w-full border border-stone-200 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-prune resize-none" />
              </div>
              <button type="submit" disabled={saving}
                className="w-full py-3 bg-prune text-white rounded-full font-semibold hover:bg-prune-dark transition-colors disabled:opacity-50">
                {saving ? t("Enregistrement...") : t("Enregistrer")}
              </button>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default Account;
